import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, Chip, Divider, Link, Popover, Stack, Typography, useTheme} from '@mui/material';
import ForceGraph2D from 'react-force-graph-2d';
import {
    deleteSimilarityEdge,
    reviewSimilarityEdge,
} from '../../../firebase/firestore/icr';
import {
    DISPLAY_RELATION_COLORS,
    DISPLAY_RELATION_LABELS,
    DISPLAY_RELATION_ORDER,
    TYPE_COLORS,
    coverageBadge,
    edgeSimilarityPercent,
    gapDisplayNumber,
    edgeDisplayRelation,
    edgeDisplayScoreLabel,
    edgeLineStyle,
    nodeKey,
    resolveEeiNavPath,
    resolveGapNavPath,
    resolveIcrNavPath,
} from './similarityLabels';

const HEIGHT_STORAGE_KEY = 'dash.similarityGraph.height';
const DEFAULT_HEIGHT = 420;
const MIN_HEIGHT = 240;
const MAX_HEIGHT = 900;
const DASH_PATTERN = [4, 2];
const NODE_RADIUS = 8;
const LABEL_FONT_SIZE = 3.6;
const PILL_PAD_X = 3.2;
const PILL_PAD_Y = 2.1;
const CHARGE_STRENGTH = -70;
const LINK_DISTANCE = 42;
const MEMBERSHIP_DISTANCE = 28;
const MEMBERSHIP_COLOR = '#90a4ae';

function nodeCanvasLabel(node) {
    if (!node) return '';
    if (node.entityType === 'GAP') return gapDisplayNumber(node) || 'Gap';
    return node.label || '';
}

function estimatePillSize(node) {
    const label = nodeCanvasLabel(node);
    const w = Math.max(label.length * LABEL_FONT_SIZE * 0.56 + PILL_PAD_X * 2, 16);
    const h = LABEL_FONT_SIZE + PILL_PAD_Y * 2 + (node?.isSubject ? 1 : 0);
    return {w, h};
}

function nodeCollideRadius(node) {
    const {w, h} = estimatePillSize(node);
    return Math.hypot(w / 2, h / 2) + 3;
}

function fillRoundRect(ctx, x, y, w, h, radius) {
    const r = Math.min(radius, w / 2, h / 2);
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(x, y, w, h, r);
    } else {
        ctx.rect(x, y, w, h);
    }
}

function applyOverlapForces(fg, nodes) {
    if (!fg || typeof fg.d3Force !== 'function') return;
    fg.d3Force('charge')?.strength?.(CHARGE_STRENGTH)?.distanceMax?.(120);
    fg.d3Force('link')?.distance?.((l) => (l.membership ? MEMBERSHIP_DISTANCE : LINK_DISTANCE))
        ?.strength?.((l) => (l.membership ? 1 : 0.75));
    fg.d3Force('center')?.strength?.(0.35);
    fg.d3Force('collide', (alpha) => {
        const n = nodes.length;
        const push = 0.65 * (alpha || 1);
        for (let i = 0; i < n; i++) {
            const a = nodes[i];
            if (a.x == null || a.y == null) continue;
            const ra = nodeCollideRadius(a);
            for (let j = i + 1; j < n; j++) {
                const b = nodes[j];
                if (b.x == null || b.y == null) continue;
                let dx = b.x - a.x;
                let dy = b.y - a.y;
                let dist = Math.hypot(dx, dy);
                if (dist === 0) {
                    dx = 0.01;
                    dy = 0.01;
                    dist = Math.hypot(dx, dy);
                }
                const minDist = ra + nodeCollideRadius(b);
                if (dist >= minDist) continue;
                const force = ((minDist - dist) / dist) * push;
                const mx = dx * force;
                const my = dy * force;
                a.vx = (a.vx || 0) - mx;
                a.vy = (a.vy || 0) - my;
                b.vx = (b.vx || 0) + mx;
                b.vy = (b.vy || 0) + my;
            }
        }
    });
    fg.d3ReheatSimulation?.();
}

function readStoredHeight() {
    try {
        const raw = localStorage.getItem(HEIGHT_STORAGE_KEY);
        const n = raw != null ? Number(raw) : NaN;
        if (Number.isFinite(n)) {
            return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(n)));
        }
    } catch {
        /* ignore */
    }
    return DEFAULT_HEIGHT;
}

function buildGraphData(payload) {
    const nodeMap = new Map();
    const links = [];

    const upsert = (raw, isSubject = false) => {
        if (!raw?.type || !raw?.id) return null;
        const id = nodeKey(raw.type, raw.id);
        const existing = nodeMap.get(id) || {
            id,
            entityType: raw.type,
            entityId: raw.id,
            label: '',
            coverage: raw.coverage,
            isSubject: false,
            number: raw.number || raw.gapNumber,
            reportId: raw.reportId,
            reportTitle: raw.reportTitle,
            reportAuthors: raw.reportAuthors,
            title: raw.title,
            text: raw.text || raw.title,
        };
        existing.isSubject = existing.isSubject || isSubject;
        existing.coverage = raw.coverage || existing.coverage;
        existing.number = gapDisplayNumber(raw) || existing.number;
        existing.reportId = raw.reportId || existing.reportId;
        existing.reportTitle = raw.reportTitle || existing.reportTitle;
        existing.reportAuthors = raw.reportAuthors || existing.reportAuthors;
        existing.title = raw.title || existing.title;
        existing.text = raw.text || raw.title || existing.text;
        if (raw.type === 'GAP') {
            const gapNum = gapDisplayNumber(raw);
            existing.number = gapNum || existing.number;
            existing.label = gapNum || 'Gap';
        } else if (raw.type === 'EEI') {
            existing.label = raw.number || 'EEI';
        } else {
            existing.label = raw.number || raw.title || 'ICR';
        }
        const badge = coverageBadge(raw.coverage);
        existing.badge = badge.score != null ? `${badge.score}%` : (
            raw.type === 'GAP' ? '' : (badge.label || '')
        );
        nodeMap.set(id, existing);
        return id;
    };

    if (payload?.subject) {
        upsert(payload.subject, true);
    }
    for (const n of payload?.nodes || []) {
        upsert(n, Boolean(n.isSubject));
    }

    const toLink = (source, dest, edge, fromType, fromId) => {
        const displayRelation = edgeDisplayRelation(edge);
        if (!displayRelation) return null;
        const target = edge?.target;
        return {
            source,
            target: dest,
            id: edge.id,
            fromType: edge.fromType || fromType,
            fromId: edge.fromId || fromId,
            toType: edge.toType || target?.type,
            toId: edge.toId || target?.id,
            relation: edge.relation,
            displayRelation,
            lineStyle: edgeLineStyle(edge),
            reviewStatus: edge.reviewStatus,
            edgeSource: edge.source,
            explanation: edge.explanation || '',
            comment: edge.comment || '',
            score: edge.score,
            displayScore: edge.displayScore != null ? edge.displayScore : null,
            color: DISPLAY_RELATION_COLORS[displayRelation] || '#90a4ae',
        };
    };

    const addLink = (fromType, fromId, edge) => {
        const target = edge?.target;
        if (!target?.type || !target?.id) return;
        const source = nodeKey(fromType, fromId);
        const dest = upsert(target);
        if (!dest || source === dest) return;
        const link = toLink(source, dest, edge, fromType, fromId);
        if (link) links.push(link);
    };

    const subjectType = payload?.subjectType || payload?.subject?.type || 'ICR';
    for (const edge of payload?.edges || []) {
        addLink(subjectType, payload.subjectId || payload.subject?.id, edge);
    }
    for (const block of payload?.eeis || []) {
        upsert({
            type: 'EEI',
            id: block.id,
            number: block.eeiNumber,
            text: block.text,
            coverage: block.coverage,
        });
        for (const edge of block.edges || []) {
            addLink('EEI', block.id, edge);
        }
    }

    // Prefer API graphEdges when present (already directed + decorated)
    if (Array.isArray(payload?.graphEdges) && payload.graphEdges.length) {
        links.length = 0;
        for (const ge of payload.graphEdges) {
            if (!ge?.fromType || !ge?.fromId || !ge?.toType || !ge?.toId) continue;
            const source = nodeKey(ge.fromType, ge.fromId);
            const dest = nodeKey(ge.toType, ge.toId);
            if (!nodeMap.has(source) || !nodeMap.has(dest)) continue;
            const link = toLink(source, dest, ge, ge.fromType, ge.fromId);
            if (link) links.push(link);
        }
    }

    addIcrEeiMembershipLinks(payload, nodeMap, links);

    // OVERLAPS is symmetric — always render both directions on the graph
    ensureBidirectionalOverlaps(links);

    return {nodes: [...nodeMap.values()], links};
}

/** Structural ICR→own-EEI links so child EEIs stay attached (not similarity). */
function addIcrEeiMembershipLinks(payload, nodeMap, links) {
    const subjectType = payload?.subjectType || payload?.subject?.type;
    const subjectId = payload?.subjectId || payload?.subject?.id;
    if (subjectType !== 'ICR' || !subjectId) return;
    const source = nodeKey('ICR', subjectId);
    if (!nodeMap.has(source)) return;

    const existing = new Set();
    for (const l of links) {
        const s = typeof l.source === 'object' ? l.source.id : l.source;
        const t = typeof l.target === 'object' ? l.target.id : l.target;
        if (s && t) {
            existing.add(`${s}|${t}`);
            existing.add(`${t}|${s}`);
        }
    }

    for (const block of payload?.eeis || []) {
        if (!block?.id) continue;
        const dest = nodeKey('EEI', block.id);
        if (!nodeMap.has(dest)) continue;
        const pair = `${source}|${dest}`;
        if (existing.has(pair)) continue;
        existing.add(pair);
        links.push({
            source,
            target: dest,
            membership: true,
            fromType: 'ICR',
            fromId: subjectId,
            toType: 'EEI',
            toId: block.id,
            relation: 'HAS_EEI',
            displayRelation: null,
            lineStyle: 'solid',
            color: MEMBERSHIP_COLOR,
            curvature: 0,
        });
    }
}

function linkPairKey(a, b, relation) {
    return `${a}|${b}|${relation || ''}`;
}

/** Ensure every OVERLAPS edge also has the reverse edge (two-sided arrows). */
function ensureBidirectionalOverlaps(links) {
    const existing = new Set();
    for (const l of links) {
        const s = typeof l.source === 'object' ? l.source.id : l.source;
        const t = typeof l.target === 'object' ? l.target.id : l.target;
        existing.add(linkPairKey(s, t, l.relation));
    }
    const toAdd = [];
    for (const l of [...links]) {
        if (l.relation !== 'OVERLAPS') continue;
        const s = typeof l.source === 'object' ? l.source.id : l.source;
        const t = typeof l.target === 'object' ? l.target.id : l.target;
        if (!s || !t || s === t) continue;
        const reverseKey = linkPairKey(t, s, 'OVERLAPS');
        if (!existing.has(reverseKey)) {
            existing.add(reverseKey);
            toAdd.push({
                ...l,
                source: t,
                target: s,
                curvature: 0.18,
                bidirectionalMirror: true,
            });
            if (l.curvature == null) l.curvature = -0.18;
        } else if (l.curvature == null && !l.bidirectionalMirror) {
            // Both directions already present — separate them visually
            const forward = s < t;
            l.curvature = forward ? -0.18 : 0.18;
        }
    }
    links.push(...toAdd);
}

function nodeNavPath(node) {
    if (!node) return null;
    if (node.entityType === 'ICR') {
        return resolveIcrNavPath({type: 'ICR', id: node.entityId, number: node.number});
    }
    if (node.entityType === 'EEI') {
        return resolveEeiNavPath({type: 'EEI', id: node.entityId, number: node.number});
    }
    if (node.entityType === 'GAP') {
        return resolveGapNavPath({
            type: 'GAP',
            id: node.entityId,
            gapNumber: node.number,
            number: node.number,
        });
    }
    return null;
}

function nodePopupTitle(node) {
    if (!node) return '';
    if (node.entityType === 'GAP') {
        const num = gapDisplayNumber(node);
        const detail = node.text || node.reportTitle || '';
        if (num && detail) return `${num} — ${detail}`;
        if (num) return num;
        return detail || 'Gap';
    }
    const title = node.title || node.text || '';
    return node.number ? `${node.number}${title ? ` — ${title}` : ''}` : (title || node.entityType);
}

function coverageLine(value, fallbackLabel) {
    if (value == null) return fallbackLabel || '—';
    return `${value}%`;
}


function edgeScoreLabel(link) {
    const onEdge = edgeDisplayScoreLabel(link);
    if (onEdge) return onEdge;
    const pct = edgeSimilarityPercent(link);
    return pct != null ? `${pct}%` : '—';
}

function nodeDisplayId(node) {
    if (!node) return '—';
    if (node.entityType === 'GAP') return gapDisplayNumber(node) || 'Gap';
    return node.number || node.label || '—';
}

function nodeDisplayTitle(node) {
    if (!node) return '';
    if (node.entityType === 'GAP') return node.text || node.reportTitle || '';
    return node.title || node.text || '';
}

function resolveLinkEndpoint(link, endpoint, nodesById) {
    const val = link?.[endpoint];
    if (val && typeof val === 'object') return val;
    if (typeof val === 'string') return nodesById.get(val) || null;
    return null;
}

function EdgeEndpointRow({node}) {
    if (!node) return null;
    const type = node.entityType;
    const id = nodeDisplayId(node);
    const title = nodeDisplayTitle(node);
    return (
        <Stack spacing={0.25} sx={{minWidth: 0}}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                    label={type}
                    size="small"
                    sx={{
                        bgcolor: `${TYPE_COLORS[type] || '#757575'}22`,
                        color: TYPE_COLORS[type] || 'text.secondary',
                        fontWeight: 600,
                    }}
                />
                {node.badge ? (
                    <Chip label={node.badge} size="small" variant="outlined"/>
                ) : null}
                <Typography
                    variant="caption"
                    sx={{fontFamily: 'monospace', color: 'text.secondary', lineHeight: 1.2}}
                >
                    {id}
                </Typography>
            </Stack>
            {title ? (
                <Typography variant="body2" sx={{wordBreak: 'break-word'}}>
                    {title}
                </Typography>
            ) : null}
        </Stack>
    );
}

/** Info popover shown when a graph edge is clicked. */
function EdgeInfoPopover({anchor, onClose, onEdgeReviewed, allowReview = true}) {
    const link = anchor?.link || null;
    const sourceNode = anchor?.sourceNode || null;
    const targetNode = anchor?.targetNode || null;
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const isDashed = link?.lineStyle === 'dashed';
    const reviewStatus = link?.reviewStatus || 'suggested';
    const edgeSource = link?.edgeSource || 'machine';
    const isManual = edgeSource === 'manual';
    const isProposed = edgeSource === 'proposed';

    const canReview = allowReview && isDashed && !isManual && !isProposed && reviewStatus === 'suggested';
    const canDelete = allowReview && !isDashed && !isProposed && Boolean(link?.id)
        && (isManual || reviewStatus === 'confirmed');

    const runAction = async (fn) => {
        if (!link || busy) return;
        setError(null);
        setBusy(true);
        try {
            await fn();
            onClose();
            if (onEdgeReviewed) await onEdgeReviewed();
        } catch (e) {
            setError(e?.message || 'Operation failed');
        } finally {
            setBusy(false);
        }
    };

    const onConfirm = () => runAction(() => reviewSimilarityEdge({
        fromType: link.fromType,
        fromId: link.fromId,
        toType: link.toType,
        toId: link.toId,
        relation: link.relation,
        status: 'confirmed',
    }));

    const onReject = () => runAction(() => reviewSimilarityEdge({
        fromType: link.fromType,
        fromId: link.fromId,
        toType: link.toType,
        toId: link.toId,
        relation: link.relation,
        status: 'rejected',
    }));

    const onDelete = () => runAction(() => deleteSimilarityEdge(link.id));

    const displayRelation = link?.displayRelation;
    const relationColor = DISPLAY_RELATION_COLORS[displayRelation] || 'text.secondary';

    return (
        <Popover
            open={Boolean(link)}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={{top: anchor?.top || 0, left: anchor?.left || 0}}
        >
            {link && (
                <Stack spacing={1} sx={{p: 1.5, minWidth: 220, maxWidth: 340}}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {isDashed ? 'System suggestion' : 'Confirmed link'}
                    </Typography>
                    <Stack spacing={0.75}>
                        <EdgeEndpointRow node={sourceNode}/>
                        <Typography variant="caption" color="text.disabled" sx={{pl: 0.5}}>
                            ↓
                        </Typography>
                        <EdgeEndpointRow node={targetNode}/>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            label={DISPLAY_RELATION_LABELS[displayRelation] || displayRelation || '—'}
                            size="small"
                            sx={{
                                bgcolor: `${relationColor}22`,
                                color: relationColor,
                                fontWeight: 600,
                            }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                            Score: {edgeScoreLabel(link)}
                        </Typography>
                    </Stack>
                    {error && (
                        <Typography variant="caption" color="error">{error}</Typography>
                    )}
                    {(canReview || canDelete) && (
                        <Stack direction="row" spacing={0.75} flexWrap="wrap">
                            {canReview && (
                                <>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        disabled={busy}
                                        onClick={onConfirm}
                                        sx={{textTransform: 'none', minWidth: 0, px: 1}}
                                    >
                                        Confirm
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        disabled={busy}
                                        onClick={onReject}
                                        sx={{textTransform: 'none', minWidth: 0, px: 1}}
                                    >
                                        Reject
                                    </Button>
                                </>
                            )}
                            {canDelete && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={busy}
                                    onClick={onDelete}
                                    sx={{textTransform: 'none', minWidth: 0, px: 1}}
                                >
                                    Delete
                                </Button>
                            )}
                        </Stack>
                    )}
                </Stack>
            )}
        </Popover>
    );
}

/** Info popover shown on first node click (ID + title link, coverage). */
function NodeInfoPopover({anchor, onClose}) {
    const node = anchor?.node || null;
    const path = nodeNavPath(node);
    const showCoverage = node && (
        node.entityType === 'ICR' || node.entityType === 'EEI' || node.entityType === 'GAP'
    );
    const coverage = node?.coverage || null;
    return (
        <Popover
            open={Boolean(node)}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={{top: anchor?.top || 0, left: anchor?.left || 0}}
        >
            {node && (
                <Stack spacing={0.75} sx={{p: 1.5, maxWidth: 340}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            label={node.entityType}
                            size="small"
                            sx={{
                                bgcolor: `${TYPE_COLORS[node.entityType] || '#757575'}22`,
                                color: TYPE_COLORS[node.entityType] || 'text.secondary',
                                fontWeight: 600,
                            }}
                        />
                        {path ? (
                            <Link
                                href={path}
                                underline="hover"
                                variant="body2"
                                fontWeight={600}
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.open(path, '_blank', 'noopener,noreferrer');
                                }}
                                sx={{cursor: 'pointer', wordBreak: 'break-word'}}
                            >
                                {nodePopupTitle(node)}
                            </Link>
                        ) : (
                            <Typography variant="body2" fontWeight={600}>
                                {nodePopupTitle(node)}
                            </Typography>
                        )}
                    </Stack>
                    {showCoverage && (
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary">
                                Analytic: {coverageLine(coverage?.overallScore, coverage?.label)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Data: {coverageLine(coverage?.dataScore, '—')}
                            </Typography>
                        </Stack>
                    )}
                    {path && (
                        <Typography variant="caption" color="text.disabled">
                            Opens in a new tab
                        </Typography>
                    )}
                </Stack>
            )}
        </Popover>
    );
}

const LINE_STYLE_COLORS = {
    solid: '#546e7a',
    dashed: '#90a4ae',
};

function FilterChip({active, color, label, count, onClick, hasFilter = false, title}) {
    const chipLabel = count != null ? `${label} (${count})` : label;
    const dimmed = hasFilter && !active;
    return (
        <Chip
            label={chipLabel}
            size="small"
            clickable
            onClick={onClick}
            title={title || undefined}
            sx={{
                bgcolor: dimmed ? 'action.disabledBackground' : color,
                color: dimmed ? 'text.disabled' : '#fff',
                fontWeight: active ? 700 : 500,
                fontSize: 11,
                height: 22,
                cursor: 'pointer',
                outline: hasFilter && active ? `2px solid ${color}` : 'none',
                outlineOffset: 1,
                transition: 'all 0.15s',
                '&:hover': {opacity: 0.85},
            }}
        />
    );
}

function countLegendItems(graph) {
    const types = {ICR: 0, EEI: 0, GAP: 0};
    for (const n of graph?.nodes || []) {
        if (Object.prototype.hasOwnProperty.call(types, n.entityType)) {
            types[n.entityType] += 1;
        }
    }
    const relations = Object.fromEntries(DISPLAY_RELATION_ORDER.map((r) => [r, 0]));
    const lineStyles = {solid: 0, dashed: 0};
    for (const l of graph?.links || []) {
        if (l.bidirectionalMirror || l.membership) continue;
        if (l.displayRelation && relations[l.displayRelation] != null) {
            relations[l.displayRelation] += 1;
        }
        const style = l.lineStyle === 'dashed' ? 'dashed' : 'solid';
        lineStyles[style] += 1;
    }
    return {types, relations, lineStyles};
}

/**
 * Force-directed similarity graph. FULL/PARTIAL edges only; solid = manual or
 * confirmed, dashed = unconfirmed machine suggestion. Coverage / score percents
 * are shown only in the click popups (not painted on nodes or edges).
 */
export default function SimilarityGraph({payload, initialHeight, footerAction, onEdgeReviewed, allowReview = true}) {
    const theme = useTheme();
    const fgRef = useRef(null);
    const wrapRef = useRef(null);
    const dragRef = useRef(null);
    const [width, setWidth] = useState(640);
    const [height, setHeight] = useState(() => initialHeight || readStoredHeight());
    const [popupAnchor, setPopupAnchor] = useState(null);
    const [edgePopupAnchor, setEdgePopupAnchor] = useState(null);
    const [visibleTypes, setVisibleTypes] = useState(() => ({
        ICR: true,
        EEI: true,
        GAP: true,
    }));
    const [visibleRelations, setVisibleRelations] = useState(() =>
        Object.fromEntries(DISPLAY_RELATION_ORDER.map((r) => [r, true])),
    );
    const [visibleLineStyles, setVisibleLineStyles] = useState({
        solid: true,
        dashed: true,
    });

    const rawData = useMemo(() => buildGraphData(payload), [payload]);
    const legendCounts = useMemo(() => countLegendItems(rawData), [rawData]);

    const data = useMemo(() => {
        const links = rawData.links.filter((l) => {
            if (l.membership) return true;
            const rel = l.displayRelation;
            if (rel && visibleRelations[rel] === false) return false;
            const style = l.lineStyle === 'dashed' ? 'dashed' : 'solid';
            if (visibleLineStyles[style] === false) return false;
            return true;
        });
        const linkedIds = new Set();
        for (const l of links) {
            const s = typeof l.source === 'object' ? l.source.id : l.source;
            const t = typeof l.target === 'object' ? l.target.id : l.target;
            if (s) linkedIds.add(s);
            if (t) linkedIds.add(t);
        }
        const nodes = rawData.nodes.filter((n) => {
            if (n.isSubject) return visibleTypes[n.entityType] !== false;
            if (visibleTypes[n.entityType] === false) return false;
            // Keep isolated subject-side EEIs even without edges
            if (n.entityType === 'EEI' && payload?.eeis?.some((b) => b.id === n.entityId)) {
                return true;
            }
            return linkedIds.has(n.id) || n.isSubject;
        });
        const nodeIds = new Set(nodes.map((n) => n.id));
        const filteredLinks = links.filter((l) => {
            const s = typeof l.source === 'object' ? l.source.id : l.source;
            const t = typeof l.target === 'object' ? l.target.id : l.target;
            return nodeIds.has(s) && nodeIds.has(t);
        });
        return {nodes, links: filteredLinks};
    }, [rawData, visibleTypes, visibleRelations, visibleLineStyles, payload]);

    const nodesById = useMemo(
        () => new Map(data.nodes.map((n) => [n.id, n])),
        [data.nodes],
    );
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect?.width;
            if (w) setWidth(Math.floor(w));
        });
        ro.observe(el);
        setWidth(Math.floor(el.clientWidth || 640));
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(HEIGHT_STORAGE_KEY, String(height));
        } catch {
            /* ignore */
        }
    }, [height]);

    useEffect(() => {
        applyOverlapForces(fgRef.current, data.nodes);
    }, [data]);

    const fitGraph = useCallback(() => {
        try {
            fgRef.current?.zoomToFit(400, 40);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        if (!fgRef.current || data.nodes.length === 0) return undefined;
        const t = setTimeout(fitGraph, 700);
        return () => clearTimeout(t);
    }, [data, fitGraph]);

    const onNodeClick = useCallback((node, event) => {
        if (!node) return;
        setEdgePopupAnchor(null);
        setPopupAnchor({
            node,
            top: event?.clientY ?? 0,
            left: event?.clientX ?? 0,
        });
    }, []);

    const onLinkClick = useCallback((link, event) => {
        if (!link || link.bidirectionalMirror || link.membership) return;
        setPopupAnchor(null);
        setEdgePopupAnchor({
            link,
            sourceNode: resolveLinkEndpoint(link, 'source', nodesById),
            targetNode: resolveLinkEndpoint(link, 'target', nodesById),
            top: event?.clientY ?? 0,
            left: event?.clientX ?? 0,
        });
    }, [nodesById]);

    const onResizeStart = useCallback((e) => {
        e.preventDefault();
        const startY = e.clientY;
        const startH = height;
        dragRef.current = {startY, startH};

        const onMove = (ev) => {
            if (!dragRef.current) return;
            const delta = ev.clientY - dragRef.current.startY;
            const next = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, dragRef.current.startH + delta));
            setHeight(next);
        };
        const onUp = () => {
            dragRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [height]);

    const toggleType = (type) => {
        setVisibleTypes((prev) => ({...prev, [type]: !prev[type]}));
    };

    const toggleRelation = (rel) => {
        setVisibleRelations((prev) => ({...prev, [rel]: !prev[rel]}));
    };

    const toggleLineStyle = (style) => {
        setVisibleLineStyles((prev) => ({...prev, [style]: !prev[style]}));
    };

    const hasNodeFilter = Object.values(visibleTypes).some((v) => v === false);
    const hasRelationFilter = Object.values(visibleRelations).some((v) => v === false)
        || Object.values(visibleLineStyles).some((v) => v === false);

    const clearNodeFilters = () => {
        setVisibleTypes({ICR: true, EEI: true, GAP: true});
    };

    const clearRelationFilters = () => {
        setVisibleRelations(Object.fromEntries(DISPLAY_RELATION_ORDER.map((r) => [r, true])));
        setVisibleLineStyles({solid: true, dashed: true});
    };

    if (!rawData.nodes.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                No graph nodes yet.
            </Typography>
        );
    }

    return (
        <Stack spacing={0}>
            <Stack
                data-testid="similarity-graph-filters"
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{px: 1.5, pt: 1, pb: 0.75, border: '1px solid', borderColor: 'divider', borderBottom: 0, borderTopLeftRadius: 1, borderTopRightRadius: 1}}
            >
                <Stack
                    data-testid="similarity-graph-node-filters"
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                >
                    <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                        Nodes:
                    </Typography>
                    {Object.entries(TYPE_COLORS).map(([type, color]) => (
                        <FilterChip
                            key={type}
                            color={color}
                            label={type}
                            count={legendCounts.types[type]}
                            active={visibleTypes[type] !== false}
                            hasFilter={hasNodeFilter}
                            onClick={() => toggleType(type)}
                        />
                    ))}
                    {hasNodeFilter && (
                        <Chip
                            label="Clear"
                            size="small"
                            variant="outlined"
                            clickable
                            onClick={clearNodeFilters}
                            aria-label="Clear node filters"
                            sx={{fontSize: 11, height: 22, cursor: 'pointer'}}
                        />
                    )}
                </Stack>
                <Divider orientation="vertical" flexItem/>
                <Stack
                    data-testid="similarity-graph-relation-filters"
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                >
                    <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                        Relations:
                    </Typography>
                    {DISPLAY_RELATION_ORDER.map((rel) => (
                        <FilterChip
                            key={rel}
                            color={DISPLAY_RELATION_COLORS[rel]}
                            label={DISPLAY_RELATION_LABELS[rel] || rel}
                            count={legendCounts.relations[rel]}
                            active={visibleRelations[rel] !== false}
                            hasFilter={hasRelationFilter}
                            onClick={() => toggleRelation(rel)}
                        />
                    ))}
                    <FilterChip
                        color={LINE_STYLE_COLORS.solid}
                        label="Confirmed"
                        count={legendCounts.lineStyles.solid}
                        active={visibleLineStyles.solid !== false}
                        hasFilter={hasRelationFilter}
                        onClick={() => toggleLineStyle('solid')}
                    />
                    <FilterChip
                        color={LINE_STYLE_COLORS.dashed}
                        label="Suggested"
                        count={legendCounts.lineStyles.dashed}
                        active={visibleLineStyles.dashed !== false}
                        hasFilter={hasRelationFilter}
                        title="Suggested by Minsky (unconfirmed)"
                        onClick={() => toggleLineStyle('dashed')}
                    />
                    {hasRelationFilter && (
                        <Chip
                            label="Clear"
                            size="small"
                            variant="outlined"
                            clickable
                            onClick={clearRelationFilters}
                            aria-label="Clear relation filters"
                            sx={{fontSize: 11, height: 22, cursor: 'pointer'}}
                        />
                    )}
                </Stack>
                {footerAction ? (
                    <Box sx={{flexShrink: 0, ml: 'auto !important'}}>{footerAction}</Box>
                ) : null}
            </Stack>
            <Box
                ref={wrapRef}
                data-testid="similarity-graph-canvas"
                sx={{
                    width: '100%',
                    height,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderBottomLeftRadius: 1,
                    borderBottomRightRadius: 1,
                    overflow: 'hidden',
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                    position: 'relative',
                }}
            >
                <ForceGraph2D
                    ref={fgRef}
                    width={width}
                    height={height}
                    graphData={data}
                    nodeRelSize={NODE_RADIUS}
                    linkDirectionalArrowLength={(l) => (l.membership ? 0 : 4)}
                    linkDirectionalArrowRelPos={1}
                    linkWidth={(l) => (l.membership ? 0.7 : 1.25)}
                    linkPointerAreaPaint={(link, color, ctx) => {
                        // Wider invisible hit area so edges are easier to click.
                        if (link.bidirectionalMirror || link.membership) return;
                        const start = link.source;
                        const end = link.target;
                        if (!start || !end || typeof start !== 'object' || typeof end !== 'object') return;
                        if (start.x == null || end.x == null) return;
                        ctx.beginPath();
                        ctx.moveTo(start.x, start.y);
                        const curv = link.curvature || 0;
                        if (curv) {
                            const cx = (start.x + end.x) / 2 + curv * (end.y - start.y);
                            const cy = (start.y + end.y) / 2 - curv * (end.x - start.x);
                            ctx.quadraticCurveTo(cx, cy, end.x, end.y);
                        } else {
                            ctx.lineTo(end.x, end.y);
                        }
                        ctx.strokeStyle = color;
                        ctx.lineWidth = Math.max(8, 8 / (fgRef.current?.zoom?.() || 1));
                        ctx.stroke();
                    }}
                    linkColor={(l) => l.color || '#90a4ae'}
                    linkCurvature={(l) => l.curvature || 0}
                    linkLineDash={(l) => (l.lineStyle === 'dashed' ? DASH_PATTERN : null)}
                    linkLabel={(l) => {
                        if (l.membership) return 'EEI of this ICR';
                        const rel = DISPLAY_RELATION_LABELS[l.displayRelation] || l.displayRelation || '—';
                        const parts = [rel];
                        if (l.lineStyle === 'dashed') parts.push('unconfirmed suggestion');
                        if (l.comment) parts.push(l.comment);
                        else if (l.explanation) parts.push(l.explanation);
                        return parts.join(' — ');
                    }}
                    nodeLabel={(n) => {
                        if (n.entityType === 'GAP') {
                            const num = gapDisplayNumber(n);
                            const lines = [num || 'Gap'];
                            if (n.reportTitle) lines.push(`FINTEL: ${n.reportTitle}`);
                            if (n.reportAuthors) lines.push(`Author: ${n.reportAuthors}`);
                            else if (n.text) lines.push(n.text);
                            return lines.join('\n');
                        }
                        return `${n.entityType} ${n.label}`;
                    }}
                    onNodeClick={onNodeClick}
                    onLinkClick={onLinkClick}
                    onEngineStop={fitGraph}
                    nodePointerAreaPaint={(node, color, ctx) => {
                        const {w, h} = estimatePillSize(node);
                        ctx.beginPath();
                        ctx.rect(node.x - w / 2, node.y - h / 2, w, h);
                        ctx.fillStyle = color;
                        ctx.fill();
                    }}
                    nodeCanvasObject={(node, ctx) => {
                        const color = TYPE_COLORS[node.entityType] || '#757575';
                        const label = nodeCanvasLabel(node);
                        ctx.font = `600 ${LABEL_FONT_SIZE}px Sans-Serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        const textW = ctx.measureText ? ctx.measureText(label).width : label.length * LABEL_FONT_SIZE * 0.56;
                        const w = Math.max(textW + PILL_PAD_X * 2, 16);
                        const h = LABEL_FONT_SIZE + PILL_PAD_Y * 2;
                        const x = node.x - w / 2;
                        const y = node.y - h / 2;
                        fillRoundRect(ctx, x, y, w, h, h / 2);
                        ctx.fillStyle = color;
                        ctx.fill();
                        if (node.isSubject) {
                            ctx.strokeStyle = theme.palette.warning.main;
                            ctx.lineWidth = 1.4;
                            ctx.stroke();
                        }
                        ctx.fillStyle = '#fff';
                        ctx.fillText(label, node.x, node.y);
                    }}
                    warmupTicks={40}
                    cooldownTicks={220}
                    enableNodeDrag
                />
                <Box
                    onMouseDown={onResizeStart}
                    title="Drag to resize height"
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 10,
                        cursor: 'ns-resize',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'transparent',
                        '&:hover': {bgcolor: 'action.hover'},
                    }}
                >
                    <Box sx={{width: 36, height: 3, borderRadius: 1, bgcolor: 'divider'}}/>
                </Box>
            </Box>

            <NodeInfoPopover anchor={popupAnchor} onClose={() => setPopupAnchor(null)}/>
            <EdgeInfoPopover
                anchor={edgePopupAnchor}
                onClose={() => setEdgePopupAnchor(null)}
                onEdgeReviewed={onEdgeReviewed}
                allowReview={allowReview}
            />
        </Stack>
    );
}
